import { BrainCircuit, CheckCircle2, ChevronDown, ChevronUp, FileSearch, History, Info, Search } from "lucide-react";
import { useState } from "react";
import type {
  MyPortfolioBehaviorSignalId,
  MyPortfolioClosedTradeReadModel,
  MyPortfolioNextReadModel,
} from "../contracts.js";
import {
  dateTime,
  duration,
  MetricCard,
  MiniMetric,
  money,
  PanelHead,
  plain,
  plainDate,
  Result,
  round,
  sampleLabel,
  ScreenIntro,
  StateScreen,
  Stock,
  tradeById,
  TradeOutcome,
} from "./MyPortfolioNextPrimitives.js";

const signalNames: Record<MyPortfolioBehaviorSignalId, string> = {
  FEAR: "خوف محتمل",
  GREED: "طمع محتمل",
  OVERCONFIDENCE: "ثقة مفرطة محتملة",
  HESITATION: "تردد محتمل",
  REVENGE: "تداول انتقامي محتمل",
};

export function HistoryScreen({ model }: { model: MyPortfolioNextReadModel }) {
  const pageSize = 10;
  const symbols = model.tradeHistory.companies.map((item) => item.symbol);
  const [selectedSymbol, setSelectedSymbol] = useState(symbols[0] ?? "");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const aggregate = model.tradeHistory.companies.find((item) => item.symbol === selectedSymbol) ?? null;
  const trades = model.tradeHistory.trades.filter((trade) => trade.symbol === selectedSymbol);
  const pageCount = Math.max(1, Math.ceil(trades.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const firstTrade = (safePage - 1) * pageSize;
  const visibleTrades = trades.slice(firstTrade, firstTrade + pageSize);
  const selectSymbol = (symbol: string) => {
    setSelectedSymbol(symbol);
    setExpanded(null);
    setPage(1);
  };
  return <div className="portfolio-x-screen">
    <ScreenIntro eyebrow="CLOSED TRADES · AUTHORITATIVE CASH LEDGER" title="تاريخ الصفقات"
      description="اختر أصلًا تعاملت معه فعليًا لترى إجماليه ثم تفاصيل كل إغلاق." />
    <div className="portfolio-x-history-filter"><label><span>الأصل أو الشركة</span><small>القائمة مشتقة من الصفقات المغلقة فقط.</small></label>
      <div><Search /><select value={selectedSymbol} onChange={(event) => selectSymbol(event.target.value)}>
        {symbols.map((symbol) => <option key={symbol} value={symbol}>{symbol}</option>)}</select><ChevronDown /></div></div>
    {aggregate ? <>
      <div className="portfolio-x-mini-totals">
        <MiniMetric label="عدد الإغلاقات" metric={aggregate.closedTradesCount} />
        <MiniMetric label="إجمالي الكمية" metric={aggregate.totalQuantity} />
        <MiniMetric label="إجمالي التكلفة" metric={aggregate.totalPurchaseValue} currency={model.identity.currency} />
        <MiniMetric label="الربح المحقق" metric={aggregate.realizedPnl} currency={model.identity.currency} />
        <MiniMetric label="متوسط المدة" text={duration(aggregate.averageDurationMs.value)} />
      </div>
      <section className="portfolio-x-panel portfolio-x-table-panel">
        <PanelHead title={`تفاصيل صفقات ${selectedSymbol}`} subtitle={`${trades.length} صفقة مغلقة · صفحة ${safePage} من ${pageCount}`} />
        <div className="portfolio-x-table-scroll"><table className="portfolio-x-table portfolio-x-trades-table"><thead><tr>
          <th>السهم / الأصل</th><th>الدخول</th><th>الخروج</th><th>الكمية</th><th>شراء / بيع</th><th>الرسوم</th>
          <th>النتيجة</th><th>المدة</th><th />
        </tr></thead><tbody>{visibleTrades.map((trade) => <TradeRows key={trade.tradeId} trade={trade}
          currency={model.identity.currency} expanded={expanded === trade.tradeId}
          onToggle={() => setExpanded(expanded === trade.tradeId ? null : trade.tradeId)} />)}</tbody></table></div>
        {pageCount > 1 && <nav className="portfolio-next-pagination" aria-label="صفحات تاريخ الصفقات">
          <button type="button" disabled={safePage === 1} onClick={() => { setExpanded(null); setPage(safePage - 1); }}>السابق</button>
          <span>عرض {firstTrade + 1}–{Math.min(firstTrade + pageSize, trades.length)} من {trades.length}</span>
          <button type="button" disabled={safePage === pageCount} onClick={() => { setExpanded(null); setPage(safePage + 1); }}>التالي</button>
        </nav>}
      </section>
    </> : <StateScreen icon={<History />} title="لا توجد صفقات مغلقة" text="لم يعثر الدفتر المقبول على إغلاقات قابلة للتسكين." />}
  </div>;
}

function TradeRows({ trade, currency, expanded, onToggle }: {
  trade: MyPortfolioClosedTradeReadModel; currency: string; expanded: boolean; onToggle(): void;
}) {
  return <>
    <tr className={expanded ? "is-expanded" : ""}>
      <td><Stock symbol={trade.symbol} subtitle={trade.direction} /></td><td>{plainDate(trade.entryAt)}</td><td>{plainDate(trade.exitAt)}</td>
      <td>{plain(trade.quantity)}</td><td>{money(trade.entryPrice, currency)} / {money(trade.exitPrice, currency)}</td>
      <td>{money(trade.fees, currency)}</td><td><Result metric={trade.realizedPnl} pct={trade.realizedPnlPct} currency={currency} /></td>
      <td>{duration(trade.durationMs.value)}</td><td><button type="button" className="portfolio-x-row-toggle" onClick={onToggle}>
        {expanded ? <ChevronUp /> : <ChevronDown />}</button></td>
    </tr>
    {expanded && <tr className="portfolio-x-detail-row"><td colSpan={9}><div className="portfolio-x-trade-detail">
      <section><header><History /><span><b>عمليات الشراء والبيع الجزئية</b><small>Execution fills</small></span></header>
        {trade.fills.length ? <ol>{trade.fills.map((fill, index) => <li key={fill.fillId}><span>{index + 1}</span>{fill.side} · {fill.quantity} · {fill.price} · {dateTime(fill.occurredAt)}</li>)}</ol>
          : <p className="portfolio-next-unknown">سجل الإغلاق حاكم، لكن تفاصيل Fills الجزئية لم تُربط بهذا الإغلاق بعد.</p>}</section>
      <section><header><BrainCircuit /><span><b>ما يمكن تعلمه</b><small>Evidence-backed only</small></span></header>
        <div className="portfolio-x-learning"><b>{plain(trade.tradeQuality)}</b><p>لا تُنسب صفة سلوكية للصفقة دون Policy وIntent مجمدين قبل القرار.</p></div></section>
      <section><header><FileSearch /><span><b>الأدلة</b><small>Fact IDs</small></span></header>
        <div className="portfolio-x-evidence-list">{trade.learningEvidence.map((fact) => <span key={fact}><CheckCircle2 />{fact}</span>)}</div></section>
    </div></td></tr>}
  </>;
}

export function StyleScreen({ model }: { model: MyPortfolioNextReadModel }) {
  const behavior = model.behavior;
  const best = tradeById(model, behavior.bestTradeId);
  const worst = tradeById(model, behavior.worstTradeId);
  return <div className="portfolio-x-screen">
    <ScreenIntro eyebrow="BEHAVIOR · RULES + EVIDENCE" title="تحليل أسلوبك"
      description="قياسات تنفيذية مؤكدة أولًا، وإشارات سلوكية فقط عندما توجد خطة وقرار سابقان قابلان للقياس." />
    <div className="portfolio-next-behavior-summary">
      <MetricCard label="نسبة النجاح" metric={behavior.winRatePct} suffix="%" />
      <MetricCard label="متوسط الربح" metric={behavior.averageWin} currency={model.identity.currency} tone="positive" />
      <MetricCard label="متوسط الخسارة" metric={behavior.averageLoss} currency={model.identity.currency} tone="negative" />
      <MetricCard label="متوسط مدة الاحتفاظ" metric={behavior.averageDurationMs} formatter={(value) => duration(value)} />
    </div>
    <div className="portfolio-x-approved-summary-lower">
      <section className="portfolio-x-approved-chart-card portfolio-x-approved-best-worst">
        <PanelHead title="أفضل وأسوأ صفقة" subtitle={`نضج العينة: ${sampleLabel(behavior.sampleState)}`} />
        <div><TradeOutcome label="أفضل صفقة" trade={best} currency={model.identity.currency} tone="is-best" />
          <TradeOutcome label="أسوأ صفقة" trade={worst} currency={model.identity.currency} tone="is-worst" /></div>
      </section>
      <section className="portfolio-x-approved-chart-card">
        <PanelHead title="ما نعرفه عن أسلوب التنفيذ" subtitle={`${behavior.sampleSize} صفقة مغلقة`} />
        <div className="portfolio-next-empty-chart"><span>النمط الغالب: <b>{plain(behavior.dominantHoldingStyle)}</b><br />الأصل الأكثر تداولًا: <b>{plain(behavior.mostTradedSymbol)}</b></span></div>
      </section>
    </div>
    <section className="portfolio-x-approved-chart-card">
      <PanelHead title="الإشارات النفسية الرقمية" subtitle="لا تشخيص؛ كل نسبة تحتاج Eligible decisions وأدلة كافية" />
      <div className="portfolio-next-signal-grid">{behavior.signals.map((signal) => <article key={signal.signalId}>
        <b>{signalNames[signal.signalId]}</b><strong>{signal.score.value === null ? "دليل غير كافٍ" : `${round(signal.score.value)}%`}</strong>
        <small>التغطية: {signal.coveragePct.value === null ? "غير قابلة للحساب" : `${round(signal.coveragePct.value)}%`}</small>
        <p>{signal.score.reasonCodes.join(" · ")}</p></article>)}</div>
      <div className="portfolio-next-summary-note"><Info /><span>كشف البروكر يثبت التنفيذ والنتائج، لكنه لا يثبت الخوف أو الطمع أو التردد وحده. تبدأ هذه الدرجات بعد ربط خطة المخاطر وIntent المجمّد قبل الصفقة؛ وهذا يمنع هيرمس أو الواجهة من اختراع وصف نفسي.</span></div>
    </section>
  </div>;
}
